// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Shell: grüner Bogen-Header (klebt oben, schrumpft beim Scrollen — Runde 10)
// + untere Navigation, portiert aus docs/referenz/trainingslog.html
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Toast from "./Toast"
import OutboxFlusher from "./OutboxFlusher"
import Celebration from "./celebration/Celebration"
import BottomNav from "./BottomNav"
import Einfuehrung from "./Einfuehrung"
import { einfuehrungGesehen } from "@/lib/einfuehrung"
import { IconFunke, IconProfil } from "./icons"

const TITLES: Record<string, string> = {
  "/training": "Training",
  "/start": "Start",
  "/log": "Nachtragen",
  "/verlauf": "Verlauf",
  "/fortschritt": "Fortschritt",
  "/plan": "Dein Plan",
  "/plaene": "Pläne",
  "/profil": "Optionen",
  "/feed": "Feed",
}

// Unterseiten des Plan-Bereichs sagen im Kopf, WO man ist (Thors Feedback Runde 8)
function pageTitle(pathname: string): string {
  const t = TITLES[pathname]
  if (t) return t
  if (pathname.startsWith("/plaene/")) {
    return pathname.endsWith("/bearbeiten") ? "Plan bearbeiten" : "Plan ansehen"
  }
  return "fitHub"
}

// Auf den Haupt-Tabs steht das heutige Datum unter dem Titel
const MIT_DATUM = new Set(["/start", "/log", "/training", "/verlauf", "/fortschritt", "/plan"])

export default function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const title = pageTitle(pathname)
  const [klein, setKlein] = useState(false)
  // Einführung beim ersten Mal (Runde 16). Steht über allem, blockiert nichts
  // dauerhaft: „Überspringen" ist immer da.
  const [zeigeEinfuehrung, setZeigeEinfuehrung] = useState(() => !einfuehrungGesehen())

  // Gescrollt wird NICHT die Seite, sondern nur dieser Kasten in der Mitte
  // (siehe base.css, „App-Schale"). Deshalb hängen Scroll-Reset und
  // Kopf-Schrumpfen an ihm statt am Fenster.
  const inhalt = useRef<HTMLDivElement>(null)

  // Seitenwechsel fängt oben an — noch VOR dem ersten Bild der neuen Seite.
  useLayoutEffect(() => {
    inhalt.current?.scrollTo({ top: 0, behavior: "instant" })
    setKlein(false)
  }, [pathname])

  // Großer Titel schrumpft beim Scrollen zur kompakten Leiste (Apple-Muster)
  useEffect(() => {
    const el = inhalt.current
    if (!el) return
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => { setKlein(el.scrollTop > 24); raf = 0 })
    }
    onScroll()
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => { el.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [])

  const datum = MIT_DATUM.has(pathname)
    ? new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })
    : null

  return (
    <>
      <header className={klein ? "app-header app-header--klein" : "app-header"}>
        <p className="eyebrow">fitHub</p>
        <h1>{title}</h1>
        {datum && <p className="header-datum">{datum}</p>}
        <div className="header-actions">
          <button className="header-btn" aria-label="Feed öffnen" onClick={() => navigate("/feed")}>
            <IconFunke size={20} />
          </button>
          <button className="header-btn" aria-label="Optionen öffnen" onClick={() => navigate("/profil")}>
            <IconProfil size={20} />
          </button>
        </div>
      </header>
      {/* Der Kasten bleibt (er scrollt), nur sein Inhalt tritt neu ein.
          Deshalb sitzt der key auf der inneren Hülle, nicht auf .wrap. */}
      <div className="wrap" ref={inhalt}>
        <div className="seite" key={pathname}>{children}</div>
      </div>
      <BottomNav />
      {zeigeEinfuehrung && <Einfuehrung onFertig={() => setZeigeEinfuehrung(false)} />}
      <Toast />
      <Celebration />
      <OutboxFlusher />
    </>
  )
}
